import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  CREATE_PROJECT_BINDINGS,
  GET_FULL_CONFIG,
  GET_COMPANIES,
  GET_PROJECT_BINDINGS,
  REMOVE_PROJECT_BINDING_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

const BINDING_KEY_TO_LABEL = {
  entity_form_binding: 'Forms',
  entity_workflow_binding: 'Workflows',
  entity_rule_binding: 'Rules',
  entity_risk_binding: 'Risk',
  entity_document_binding: 'Documents',
  entity_template_binding: 'Templates',
  entity_sla_binding: 'SLA',
  entity_notification_binding: 'Notifications',
  entity_override_binding: 'Overrides',
  entity_audit_binding: 'Audit',
  entity_invoicing_binding: 'Invoicing',
  entity_lifecycle_binding: 'Lifecycle'
};

export default {
  name: 'GlobalOverview',
  mixins: [alert_mixin],

  data: () => ({
      featured_blueprint: {
        id: null,
        title: '—',
        version: '—',
        modified: '—',
        description: '',
        status: '—',
        status_class: 'secondary',
        binding_count: 0,
        subsystem_bindings: [],
        active_projects: []
      },
      secondary_blueprint: {
        id: null,
        title: '—',
        version: '—',
        modified: '—',
        description: '',
        status: '—',
        status_class: 'secondary',
        binding_count: 0,
        subsystem_bindings: [],
        active_projects: []
      },
      selected_blueprint_title: null,
      selected_blueprint_id: null,

      companies: [],
      companies_loading: false,
      companies_error: null,

      assign_project_ids: [],
      assign_blueprint: null,

      assign_entity_config_id: null,
      assign_bindings_loading: false,
      project_binding_by_project_id: {},
      project_toggle_loading_by_project_id: {},

      project_bindings_by_entity: {},
      full_config_by_entity_type: {},

      bindings_loading: false,
      bindings_error: null,

      entity_types_loading: false,
      entity_types_error: null,
      status_filter: 'all',
      sort_option: 'updated_desc',

      blueprint_library: []
  }),

  mounted() {
    this.fetch_companies();
    this.fetch_entity_types();
  },

  computed: {
    featured_active_projects_display() {
      const id = this.featured_blueprint?.id;
      const bindings = id ? this.project_bindings_by_entity[String(id)] : null;

      const projects = Array.isArray(bindings) && bindings.length
        ? bindings.map((binding) => ({
          id: binding?.project,
          name: binding?.project_name
        }))
        : (Array.isArray(this.companies) ? this.companies : []);

      const shown = projects.slice(0, 2).map(project => ({
        key: String(project?.id ?? Math.random()),
        id: project?.id,
        label: this.project_short_label(project),
        name: this.project_display_name(project),
        is_more: false
      }));

      if (projects.length > shown.length) {
        shown.push({
          key: 'more',
          label: `+${projects.length - shown.length}`,
          is_more: true
        });
      }

      return shown;
    },

    selected_projects_count() {
      return Array.isArray(this.assign_project_ids) ? this.assign_project_ids.length : 0;
    },
    selected_projects_label() {
      const ids = Array.isArray(this.assign_project_ids) ? this.assign_project_ids : [];
      if (!ids.length) return '';
      const companies = Array.isArray(this.companies) ? this.companies : [];
      const names = ids
        .map((id) => companies.find((p) => String(p?.id) === String(id)))
        .map((p) => this.project_display_name(p))
        .filter(Boolean);

      return names.join(', ');
    },
    filtered_blueprint_library() {
      const normalized_status_filter = String(this.status_filter || 'all');
      const normalized_sort_option = String(this.sort_option || 'updated_desc');

      const filtered_items = this.blueprint_library.filter((item) => {
        if (normalized_status_filter === 'active') {
          return item.status === 'Active';
        }

        if (normalized_status_filter === 'inactive') {
          return item.status === 'Inactive';
        }

        return true;
      });

      const sorted_items = [...filtered_items].sort((left, right) => {
        if (normalized_sort_option === 'name_asc') {
          return String(left?.title || '').localeCompare(String(right?.title || ''));
        }

        if (normalized_sort_option === 'name_desc') {
          return String(right?.title || '').localeCompare(String(left?.title || ''));
        }

        return 0;
      });

      return sorted_items;
    }
  },

  methods: {
    parse_api_data(data) {
      let result = data;

      if (typeof data === 'string') {
        try {
          result = JSON.parse(data);
        } catch {
          result = data;
        }
      }

      return result;
    },

    format_relative_date(date) {
      if (!date) return 'Modified recently';

      const diff = Date.now() - new Date(date);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      return hours < 24
        ? `Modified ${hours || 1} hours ago`
        : `Modified ${days || 1} days ago`;
    },

    project_display_name(project) {
      return project?.name || project?.project_name || project?.project?.name;
    },

    project_short_label(p) {
      const name = this.project_display_name(p) || '';
      const parts = name.split(' ');
      return (parts[0]?.[0] + (parts[1]?.[0] || '')).toUpperCase() || 'PR';
    },

    go_to_project_management(id) {
      if (!this.$router) return;
      this.$router.push({
        name: 'ProjectManagement',
        query: id ? { highlight: String(id) } : undefined
      });
    },

    fetch_companies() {
      this.companies_loading = true;
      return axios
        .get(GET_COMPANIES, get_request_config())
        .then((res) => {
          const data = this.parse_api_data(res.data);
          this.companies = data?.data || data?.projects || [];
        })
        .catch((err) => {
          this.companies = [];
          this.show_error(err?.message || 'Failed to load projects');
        })
        .finally(() => {
          this.companies_loading = false;
        });
    },

    binding_summary_to_badges(summary = {}) {
      const result = Object.entries(BINDING_KEY_TO_LABEL)
        .map(([k, label]) => {
          const count = Number(summary[k]);
          return count ? `${label} (${count})` : null;
        })
        .filter(Boolean);

      return result.length ? result : ['No bindings'];
    },

    binding_summary_to_count(summary = {}) {
      return Object.keys(BINDING_KEY_TO_LABEL)
        .reduce((total, key) => total + (Number(Reflect.get(summary ?? {}, key)) || 0), 0);
    },

    bindings_to_count(bindings) {
      if (!Array.isArray(bindings) || !bindings.length) return 0;

      if (
        bindings.length === 1 &&
        typeof bindings[0] === 'string' &&
        bindings[0].trim().toLowerCase() === 'no bindings'
      ) {
        return 0;
      }

      let total_count = 0;
      let has_counts = false;

      for (const binding of bindings) {
        if (typeof binding !== 'string') continue;
        const count_match = binding.match(/\((\d+)\)\s*$/);
        if (count_match) {
          has_counts = true;
          total_count += Number(count_match[1]) || 0;
        }
      }

      return has_counts ? total_count : bindings.length;
    },

    fetch_full_config_by_entity_type(id) {
      if (!id) return Promise.resolve(null);

      if (this.full_config_by_entity_type[id]) {
        return Promise.resolve(this.full_config_by_entity_type[id]);
      }

      return axios
        .get(GET_FULL_CONFIG, get_request_config({
          params: { entity_type_id: id }
        }))
        .then((res) => {
          const data = this.parse_api_data(res.data)?.data;

          this.full_config_by_entity_type[id] = data;
          return data;
        })
        .catch(() => {
          return null;
        });
    },

    async refresh_featured_bindings() {
      const id = this.featured_blueprint?.id;
      if (!id) return;

      this.bindings_loading = true;

      try {
        const full = await this.fetch_full_config_by_entity_type(id);
        const summary = full?.entity_config?.entity_schema?.binding_summary;

        this.featured_blueprint.subsystem_bindings = this.binding_summary_to_badges(summary);
        this.featured_blueprint.binding_count = this.binding_summary_to_count(summary);
      } catch {
        this.bindings_error = 'Failed to load bindings';
        this.show_error(this.bindings_error);
      } finally {
        this.bindings_loading = false;
      }
    },

    map_entity_type_to_blueprint(entityType, index = 0) {
      const blueprint = {
        id: entityType?.id,
        title: entityType?.entity_type_name || entityType?.name || `Entity ${index + 1}`,
        version: entityType?.version || `VER ${index + 1}`,
        modified: this.format_relative_date(
          entityType?.date_updated || entityType?.date_created
        ),
        description: entityType?.description || '',
        binding_count: this.bindings_to_count(entityType?.subsystem_bindings || []),
        subsystem_bindings: entityType?.subsystem_bindings || [],
        status: entityType?.is_active ? 'Active' : 'Inactive',
        status_class: entityType?.is_active ? 'success' : 'secondary',
        disabled: false
      };

      return blueprint;
    },

    initialize_featured_from_library() {
      if (this.selected_blueprint_title || !this.blueprint_library.length) return;

      this.featured_blueprint = this.blueprint_library[0];
      this.secondary_blueprint = this.blueprint_library[1] || {};
      this.selected_blueprint_title = this.featured_blueprint.title;
      this.selected_blueprint_id = this.featured_blueprint?.id ?? null;

      this.refresh_featured_bindings();
    },

    async fetch_entity_types() {
      this.entity_types_loading = true;

      try {
        const res = await axios.get(VIEW_ENTITY_TYPE_URL, get_request_config());
        const data = this.parse_api_data(res.data);

        this.blueprint_library = (data?.data || []).map(this.map_entity_type_to_blueprint);

        this.initialize_featured_from_library();
      } catch {
        this.entity_types_error = 'Failed to load entity types';
        this.show_error(this.entity_types_error);
      } finally {
        this.entity_types_loading = false;
      }
    },

    async open_assign_modal(blueprint) {
      this.assign_blueprint = blueprint || this.featured_blueprint;
      this.assign_entity_config_id = null;
      this.assign_project_ids = [];
      this.project_binding_by_project_id = {};
      this.project_toggle_loading_by_project_id = {};

      if (!this.companies.length) {
        await this.fetch_companies();
      }

      await this.load_assign_project_bindings();
    },

    is_project_selected(projectId) {
      if (projectId == null) return false;
      const ids = Array.isArray(this.assign_project_ids) ? this.assign_project_ids : [];
      return ids.some((id) => String(id) === String(projectId));
    },

    is_project_toggle_loading(projectId) {
      return Boolean(this.project_toggle_loading_by_project_id?.[String(projectId)]);
    },

    project_meta(project) {
      if (!project) return '';
      const type = project?.project_type?.name || project?.type?.name || project?.project_type_name || '';
      const status = project?.status || project?.project_status || '';
      const left = type ? String(type) : '';
      const right = status ? String(status) : '';
      if (left && right) return `${left} • ${right}`;
      return left || right;
    },

    async fetch_project_bindings(projectId) {
      const response = await axios.get(
        GET_PROJECT_BINDINGS,
        get_request_config({
          params: { project_id: projectId }
        })
      );
      const parsed = this.parse_api_data(response.data);
      return Array.isArray(parsed?.data) ? parsed.data : [];
    },

    async load_assign_project_bindings() {
      if (!this.assign_blueprint?.id) return;

      this.assign_bindings_loading = true;
      this.assign_entity_config_id = null;
      this.assign_project_ids = [];
      this.project_binding_by_project_id = {};

      try {
        const full = await this.fetch_full_config_by_entity_type(this.assign_blueprint.id);
        const entityConfigId = full?.entity_config?.id;
        if (!entityConfigId) return;

        this.assign_entity_config_id = entityConfigId;

        const companies = Array.isArray(this.companies) ? this.companies : [];
        const checks = await Promise.all(
          companies.map(async (project) => {
            try {
              const bindings = await this.fetch_project_bindings(project.id);
              const match = bindings.find((b) =>
                String(b?.entity_config) === String(entityConfigId) && b?.is_active !== false
              );

              return {
                projectId: project.id,
                bindingId: match?.id || null
              };
            } catch {
              return { projectId: project.id, bindingId: null };
            }
          })
        );

        const assigned = [];
        const map = {};

        for (const entry of checks) {
          if (entry?.bindingId) {
            assigned.push(String(entry.projectId));
            map[String(entry.projectId)] = Number(entry.bindingId);
          }
        }

        this.assign_project_ids = assigned;
        this.project_binding_by_project_id = map;
      } catch (e) {
        this.show_error('Failed to load assigned projects');
      } finally {
        this.assign_bindings_loading = false;
      }
    },

    async set_project_assignment(project, shouldAssign) {
      const projectId = project?.id;
      if (!projectId) return;
      if (!this.assign_blueprint?.id) return;

      if (!this.assign_entity_config_id) {
        await this.load_assign_project_bindings();
        if (!this.assign_entity_config_id) return;
      }

      const key = String(projectId);
      this.project_toggle_loading_by_project_id = {
        ...(this.project_toggle_loading_by_project_id || {}),
        [key]: true
      };

      try {
        const currentlyAssigned = this.is_project_selected(projectId);
        if (shouldAssign === currentlyAssigned) return;

        if (shouldAssign) {
          const res = await axios.post(
            CREATE_PROJECT_BINDINGS,
            {
              entity_config_id: this.assign_entity_config_id,
              project_id: String(projectId)
            },
            get_request_config()
          );

          const data = this.parse_api_data(res.data);
          if (data?.status !== 'success') {
            throw new Error('Assignment failed');
          }

          // Refresh binding id for this project so we can unassign later.
          const bindings = await this.fetch_project_bindings(projectId);
          const match = bindings.find((b) =>
            String(b?.entity_config) === String(this.assign_entity_config_id) && b?.is_active !== false
          );
          if (match?.id) {
            this.project_binding_by_project_id = {
              ...(this.project_binding_by_project_id || {}),
              [key]: Number(match.id)
            };
          }

          this.assign_project_ids = [
            ...(Array.isArray(this.assign_project_ids) ? this.assign_project_ids : []),
            key
          ].filter((v, idx, arr) => arr.indexOf(v) === idx);
        } else {
          const bindingId = this.project_binding_by_project_id?.[key];
          if (bindingId) {
            const res = await axios.post(
              REMOVE_PROJECT_BINDING_URL,
              { binding_id: Number(bindingId) },
              get_request_config()
            );
            const data = this.parse_api_data(res.data);
            if (data?.status !== 'success') {
              throw new Error('Unassign failed');
            }
          }

          this.assign_project_ids = (Array.isArray(this.assign_project_ids) ? this.assign_project_ids : [])
            .filter((id) => String(id) !== key);

          const nextMap = { ...(this.project_binding_by_project_id || {}) };
          delete nextMap[key];
          this.project_binding_by_project_id = nextMap;
        }
      } catch (e) {
        this.show_error(e?.message || 'Failed to update assignment');
        await this.load_assign_project_bindings();
      } finally {
        this.project_toggle_loading_by_project_id = {
          ...(this.project_toggle_loading_by_project_id || {}),
          [key]: false
        };
      }
    },

    on_project_toggle(project, evt) {
      const checked = Boolean(evt?.target?.checked);
      return this.set_project_assignment(project, checked);
    },

    select_blueprint(item) {
      if (!item || item.disabled) return;

      const prev = this.featured_blueprint;
      this.featured_blueprint = item;
      this.secondary_blueprint = prev;

      this.selected_blueprint_title = item.title;
      this.selected_blueprint_id = item?.id ?? null;
      this.refresh_featured_bindings();
    },

    feature_secondary_blueprint() {
      if (!this.secondary_blueprint?.id) return;

      const temp = this.featured_blueprint;
      this.featured_blueprint = this.secondary_blueprint;
      this.secondary_blueprint = temp;

      this.selected_blueprint_title = this.featured_blueprint.title;
      this.selected_blueprint_id = this.featured_blueprint?.id ?? null;
      this.refresh_featured_bindings();
    },

    redirect_to_create_entity() {
        this.$router.push({
            path: '/entity-configs',
        });
    },
  }
};
